// Additive WebAudio tap for the video export. Captures the FULL playback mix
// (voice + music + SFX + censor bleep) without altering the speaker output.
//
// Design (see the spec): there is no master mix node — voice runs through
// lipSync's analyser → ctx.destination, while music/SFX/bleeper each connect to a
// destination passed at construction. So:
//   • voice  → tap via LipSyncAnalyser.connectTap(streamDest)  (parallel; existing
//              analyser → ctx.destination wiring untouched)
//   • music / SFX / bleeper → constructed with `bus` (this tap's unity-gain bus)
//              instead of ctx.destination. The bus fans to BOTH ctx.destination
//              (speakers) AND the streamDest (tap). A unity-gain GainNode is
//              audibly transparent, so the mix the user hears is unchanged.
// When NOT capturing, none of this exists and every component connects straight to
// ctx.destination → photo + deck audio is byte-identical.

export interface AudioTap {
  /** The capture sink — its `.stream` carries the summed mix for the recorder. */
  streamDest: MediaStreamAudioDestinationNode;
  /** Unity-gain bus to pass as the `destination` for music/SFX/bleeper while capturing. */
  bus: GainNode;
  /** The captured MediaStream (a single audio track). */
  stream: MediaStream;
  /** Parallel-connect a source terminal (e.g. the voice analyser) to the tap. */
  connectSource(node: AudioNode): void;
  /** Clone the audio track for per-export-run use (MediaStreamTrackProcessor locks the original). */
  cloneTrack(): MediaStreamTrack | null;
  dispose(): void;
}

export function createAudioTap(ctx: AudioContext): AudioTap {
  const streamDest = ctx.createMediaStreamDestination();
  const bus = ctx.createGain();
  bus.gain.value = 1; // unity → transparent to the speaker mix
  bus.connect(ctx.destination); // speakers (unchanged audible output)
  bus.connect(streamDest);      // capture tap

  return {
    streamDest,
    bus,
    stream: streamDest.stream,
    connectSource(node: AudioNode) {
      node.connect(streamDest);
    },
    cloneTrack() {
      const t = streamDest.stream.getAudioTracks()[0];
      return t ? t.clone() : null;
    },
    dispose() {
      try { bus.disconnect(); } catch { /* already gone */ }
    },
  };
}
